/**
 * Number Base Converter Tool
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

type Base = 2 | 8 | 10 | 16;

function parseBigIntBase(input: string, base: number): bigint {
  const digits = '0123456789abcdefghijklmnopqrstuvwxyz';
  let str = input.toLowerCase().trim();
  let negative = false;
  if (str.startsWith('-')) { negative = true; str = str.slice(1); }
  else if (str.startsWith('+')) { str = str.slice(1); }
  if (str.length === 0) throw ToolError.validation('Empty input');
  let result = 0n;
  for (const ch of str) {
    const d = digits.indexOf(ch);
    if (d < 0 || d >= base) throw ToolError.validation(`Invalid digit '${ch}' for base ${base}`);
    result = result * BigInt(base) + BigInt(d);
  }
  return negative ? -result : result;
}

function parseAutoInt(text: string): { value: bigint; base: Base } {
  const trimmed = text.trim();
  if (/^0x[0-9a-fA-F]+$/.test(trimmed)) return { value: BigInt(trimmed), base: 16 };
  if (/^0b[01]+$/.test(trimmed)) return { value: BigInt(trimmed), base: 2 };
  if (/^0o[0-7]+$/.test(trimmed)) return { value: BigInt(trimmed), base: 8 };
  if (/^[+-]?[0-9]+$/.test(trimmed)) return { value: BigInt(trimmed), base: 10 };

  // Fallback: treat as base10 bigint
  return { value: BigInt(trimmed), base: 10 };
}

function toStringBase(value: bigint, base: Base, uppercase: boolean): string {
  const out = value.toString(base);
  return uppercase ? out.toUpperCase() : out.toLowerCase();
}

export const BaseConverterTool: ITransformerTool = {
  id: 'base-converter',
  name: 'Number Base Converter',
  description: 'Convert numbers between binary, octal, decimal, and hex',
  version: '1.0.0',
  type: 'transformer',
  category: 'Converters',
  keywords: ['number', 'base', 'convert', 'hex', 'binary', 'octal', 'decimal'],

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'inputBase',
      type: 'select',
      label: 'Input Base',
      defaultValue: 'auto',
      options: ['auto', '2', '8', '10', '16'],
      description: 'Auto detects 0x/0b/0o prefixes, otherwise assumes decimal',
    },
    {
      name: 'outputBase',
      type: 'select',
      label: 'Output Base',
      defaultValue: '16',
      options: ['2', '8', '10', '16'],
      description: 'Base to convert to',
    },
    {
      name: 'uppercase',
      type: 'boolean',
      label: 'Uppercase',
      defaultValue: true,
      description: 'Uppercase hex output',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const text = String(input ?? '').trim();
    if (!text) return '';

    const outputBase = parseInt(String(config.outputBase ?? '16'), 10) as Base;
    const uppercase = config.uppercase !== false;
    const inputBaseRaw = String(config.inputBase ?? 'auto');

    try {
      let value: bigint;

      if (inputBaseRaw === 'auto') {
        ({ value } = parseAutoInt(text));
      } else {
        const inputBase = parseInt(inputBaseRaw, 10) as Base;
        const normalized = text.replace(/^0x/i, '').replace(/^0b/i, '').replace(/^0o/i, '');
        value = parseBigIntBase(normalized, inputBase);
      }

      return toStringBase(value, outputBase, uppercase);
    } catch (e) {
      if (e instanceof ToolError) throw e;
      const msg = e instanceof Error ? e.message : 'Invalid number';
      throw ToolError.validation(msg);
    }
  },
};
